// The welcome checklist (checklist.js has the rules), in the product's own look, as
// Accelerator.dc.html's "First visit" Tweak draws it: the title, how many things are left, Hide
// this, and each item with its tick box and, until it's ticked, its action. Ticks and Hide are saved
// as they go; while staff view as a member nothing is saved and every control is off.
//
//   <WelcomeChecklist product={productRow} />     inside a ProductDataProvider
//   <WelcomeChecklist product={productRow} goalExists />     a mastermind's, with whether they have a goal (null while
//                                                            that isn't known yet: nothing is drawn until it is)
//
// The product routes draw it at the top of every product's home, so a product's own home screen
// doesn't draw it again.
//
// A mastermind's (the Accelerator critique's member 12, 20 Oct; checklist.js has the rules): the calendar item adds the
// 1 session it names, Draft your goal takes them to the goal box (#acc-goal) and its textarea or Change your goal, and
// the home's twins tick it through useChecklistTick. Every other type's is as it was.
import { useCallback, useMemo, useRef, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { navigate } from '../lib/router.jsx'
import { downloadIcs, downloadIcsAll } from '../lib/calendar.js'
import { useViewingAs, readOnlyControl } from '../lib/viewAs.jsx'
import { useProductData } from './ProductData.jsx'
import { checklistModel, itemByKind } from './checklist.js'
import { myIntake } from './model.js'
import { hideWelcome, tickChecklist } from './data.js'

const SAVE_FAILED = "That didn't save. Check your connection and try again."

// Ticks the checklist item a twin on a mastermind's home stands for (its kind: 'whatsapp', 'calendar'), as that
// item's own button would: saved at once, never while staff view as a member, nothing if there's no such item or
// it's ticked already. A failed save is put back quietly: the item is still there to tick.
export function useChecklistTick() {
  const { me } = useMember()
  const { data, patch } = useProductData() || {}
  const viewingAs = useViewingAs()
  const personId = me?.person?.id
  return useCallback(kind => {
    if (viewingAs || !data || !personId || !patch) return
    const item = itemByKind(data.checklist, kind)
    if (!item || (data.checklistTicks || []).includes(item.id)) return
    patch(d => ({ ...d, checklistTicks: [...new Set([...(d.checklistTicks || []), item.id])] }))
    tickChecklist(personId, item.id, true).catch(e => {
      if (e?.code === '23505') return
      patch(d => ({ ...d, checklistTicks: (d.checklistTicks || []).filter(x => x !== item.id) }))
    })
  }, [viewingAs, data, personId, patch])
}

// Takes them to a mastermind's goal box: its textarea, or Change your goal on a draft
function toGoal() {
  const box = document.getElementById('acc-goal')
  if (!box) return
  box.scrollIntoView({ block: 'start', behavior: 'smooth' })
  const field = box.querySelector('textarea') || box.querySelector('.acc-goal-change')
  if (field) field.focus({ preventScroll: true })
}

export function WelcomeChecklist({ product, goalExists = false }) {
  const { me } = useMember()
  const { data, patch } = useProductData()
  const nowMs = useNow()
  const viewingAs = useViewingAs()
  const [line, setLine] = useState(null)
  // ticked everything before this visit: it stays hidden; ticked the last 1 now: "All done" shows
  const doneAtLoad = useRef(null)
  // their first visit: the product's rows were read before it was ever opened (the frame writes
  // last_opened_at as it opens, without changing the rows this visit read)
  const firstAtLoad = useRef(null)
  if (data && firstAtLoad.current === null) firstAtLoad.current = !data.state?.last_opened_at

  const model = useMemo(() => (data ? checklistModel({
    product, items: data.checklist, ticks: data.checklistTicks, state: data.state, sessions: data.sessions,
    whatsappUrl: data.whatsappUrl, nowMs, doneNow: doneAtLoad.current === false, firstVisit: firstAtLoad.current,
    group: myIntake(data)?.name || null,
    // Start here, where they're up to (/poyt/start-here), for the item that tells them to do it
    startHereHref: (data.steps || []).some(st => st.section === 'start_here' && st.is_published !== false) ? `${product.path || ''}/start-here` : null,
    mastermind: product.type === 'mastermind', goalExists,
  }) : null), [data, product, nowMs, goalExists])
  // a mastermind's waits until it knows whether there's a goal, so a goal item already done isn't first drawn as not
  if (product.type === 'mastermind' && goalExists === null) return null
  if (model && doneAtLoad.current === null) doneAtLoad.current = model.items.length > 0 && model.items.every(i => i.done)
  if (!model || !model.show) return null
  const personId = me.person.id

  function setTick(id, done) {
    if (viewingAs) return
    setLine(null)
    patch(d => ({ ...d, checklistTicks: done ? [...new Set([...d.checklistTicks, id])] : d.checklistTicks.filter(x => x !== id) }))
    tickChecklist(personId, id, done).catch(e => {
      // already ticked elsewhere is still ticked
      if (done && e?.code === '23505') return
      patch(d => ({ ...d, checklistTicks: done ? d.checklistTicks.filter(x => x !== id) : [...d.checklistTicks, id] }))
      setLine(SAVE_FAILED)
    })
  }

  function act(item) {
    if (viewingAs) return
    const a = item.action
    if (a.kind === 'whatsapp' || a.kind === 'link') {
      if (a.url.startsWith('/')) navigate(a.url)
      else window.open(a.url, '_blank', 'noopener,noreferrer')
    } else if (a.kind === 'account') {
      navigate('/account')
    } else if (a.kind === 'calendar' && a.one) {
      // the 1 session the item names, as the next session's card adds it, which then says Added
      const s = model.upcoming[0]
      downloadIcs({
        uid: s.id, title: `${product.name}, ${s.title}`, startMs: Date.parse(s.starts_at),
        endMs: Date.parse(s.starts_at) + (s.duration_minutes || 60) * 60e3, path: product.path || '/',
      })
      patch(d => ({ ...d, calendarAdded: true }))
    } else if (a.kind === 'calendar') {
      downloadIcsAll({
        name: product.name,
        events: model.upcoming.map(s => ({
          uid: s.id, title: `${product.name}, ${s.title}`, startMs: Date.parse(s.starts_at),
          endMs: Date.parse(s.starts_at) + (s.duration_minutes || 60) * 60e3, path: product.path || '/',
        })),
      })
    } else if (a.kind === 'goal') {
      // done once a goal is saved, so nothing is ticked here
      toGoal()
      return
    }
    if (!item.done) setTick(item.id, true)
  }

  function hide() {
    if (viewingAs) return
    const at = new Date().toISOString()
    patch(d => ({ ...d, state: { ...(d.state || { product_id: product.id }), welcome_hidden_at: at } }))
    hideWelcome(personId, product.id).catch(() => {
      patch(d => ({ ...d, state: { ...d.state, welcome_hidden_at: null } }))
      setLine(SAVE_FAILED)
    })
  }

  return (
    <section className="pp-welcome" aria-labelledby="pp-welcome-title">
      <div className="pp-welcome-head">
        <div className="pp-welcome-titles">
          <h2 className="pp-welcome-h2" id="pp-welcome-title">{model.title}</h2>
          <span className="pp-welcome-line">{model.line} {model.count}</span>
        </div>
        <button type="button" className="pp-welcome-hide" onClick={hide} {...readOnlyControl(viewingAs)}>Hide this</button>
      </div>
      <div className="pp-welcome-grid">
        {model.items.map(item => (
          <div key={item.id} className={item.done ? 'pp-welcome-item pp-welcome-item--done' : 'pp-welcome-item'}>
            <input type="checkbox" aria-label={`Mark as done: ${item.text}`} checked={item.done}
              onChange={e => setTick(item.id, e.target.checked)} {...readOnlyControl(viewingAs)} />
            <span className="pp-welcome-body">
              <span className="pp-welcome-text">{item.text}</span>
              {item.action && !item.done && (
                <button type="button" className="pp-welcome-act" onClick={() => act(item)} {...readOnlyControl(viewingAs)}>{item.action.label}</button>
              )}
            </span>
          </div>
        ))}
      </div>
      {line && <p className="pp-welcome-msg" role="alert">{line}</p>}
    </section>
  )
}
